import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Upload, 
  Folder, 
  Image, 
  Trash2, 
  Copy, 
  Check, 
  Search, 
  X,
  FileImage,
  Grid,
  List,
  Download,
  ExternalLink
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminMedia = () => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const folders = [
    { id: 'all', name: t('admin.media_page.folder_all', 'Všetky'), icon: Folder },
    { id: 'products', name: t('admin.media_page.folder_products', 'Produkty'), icon: FileImage },
    { id: 'blog', name: 'Blog', icon: FileImage },
    { id: 'categories', name: t('admin.media_page.folder_categories', 'Kategórie'), icon: FileImage },
    { id: 'gallery', name: t('admin.media_page.folder_gallery', 'Galéria'), icon: FileImage },
  ];

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('sunify_token');
      const params = new URLSearchParams();
      if (selectedFolder !== 'all') params.append('folder', selectedFolder);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API_URL}/api/media?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFiles(response.data.files);
    } catch (error) {
      console.error('Error fetching media:', error);
      toast.error(t('admin.media_page.fetch_error', 'Nepodarilo sa načítať súbory'));
    } finally {
      setLoading(false);
    }
  }, [selectedFolder, searchQuery]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUpload = async (uploadFiles) => {
    if (!uploadFiles || uploadFiles.length === 0) return;
    
    setUploading(true);
    const formData = new FormData();
    
    for (const file of uploadFiles) {
      formData.append('files', file);
    }
    
    try {
      const token = localStorage.getItem('sunify_token');
      const response = await axios.post(
        `${API_URL}/api/media/upload?folder=${selectedFolder}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const successful = response.data.results.filter(r => r.success).length;
      const failed = response.data.results.filter(r => !r.success).length;
      
      if (successful > 0) {
        toast.success(t('admin.media_page.uploaded', 'Nahraných {{n}} súborov', { n: successful }));
      }
      if (failed > 0) {
        toast.error(t('admin.media_page.upload_failed', 'Nepodarilo sa nahrať {{n}} súborov', { n: failed }));
      }
      
      fetchFiles();
    } catch (error) {
      console.error('Error uploading:', error);
      toast.error(t('admin.media_page.upload_error', 'Chyba pri nahrávaní'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(t('admin.media_page.delete_confirm', 'Naozaj chcete odstrániť {{name}}?', { name: filename }))) return;
    
    try {
      const token = localStorage.getItem('sunify_token');
      await axios.delete(`${API_URL}/api/media/${filename}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('admin.media_page.deleted', 'Súbor odstránený'));
      fetchFiles();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error(t('admin.media_page.delete_error', 'Nepodarilo sa odstrániť súbor'));
    }
  };

  const copyUrl = (file) => {
    const fullUrl = `${API_URL}${file.url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(file.filename);
    toast.success(t('admin.media_page.url_copied', 'URL skopírovaná!'));
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleUpload(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleUpload(selectedFiles);
    e.target.value = '';
  };

  return (
    <div className="p-6" data-testid="admin-media-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📁 {t('admin.media_page.title', 'Médiá knižnica')}</h1>
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="h-4 w-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Upload Area */}
      <div
        className={`
          border-2 border-dashed rounded-xl p-8 mb-6 text-center transition-all
          ${dragOver ? 'border-orange-500 bg-orange-50' : 'border-gray-300 hover:border-orange-400'}
        `}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-testid="upload-dropzone"
      >
        <Upload className={`h-12 w-12 mx-auto mb-4 ${dragOver ? 'text-orange-500' : 'text-gray-400'}`} />
        <p className="text-lg font-medium text-gray-700 mb-2">
          {uploading ? t('admin.media_page.uploading', 'Nahrávanie...') : t('admin.media_page.drop_here', 'Pretiahnite súbory sem')}
        </p>
        <p className="text-sm text-gray-500 mb-4">alebo</p>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={handleFileInput}
          disabled={uploading}
        />
        <Button 
          variant="default" 
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-2" />
          {t('admin.media_page.choose_files', 'Vybrať súbory')}
        </Button>
        <p className="text-xs text-gray-400 mt-4">
          {t('admin.media_page.formats', 'Podporované formáty: JPG, PNG, GIF, WebP, SVG')}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* Folders */}
        <div className="flex gap-2 flex-wrap">
          {folders.map((folder) => (
            <Button
              key={folder.id}
              variant={selectedFolder === folder.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedFolder(folder.id)}
              className="flex items-center gap-2"
            >
              <folder.icon className="h-4 w-4" />
              {folder.name}
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={t('admin.media_page.search_ph', 'Hľadať podľa názvu...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Files Count */}
      <p className="text-sm text-gray-500 mb-4">
        {t('admin.media_page.total_files', 'Celkom súborov')}: <span className="font-medium">{files.length}</span>
      </p>

      {/* Loading */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : files.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Image className="h-16 w-16 mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">{t('admin.media_page.no_files', 'Žiadne súbory')}</p>
        </div>
      ) : viewMode === 'grid' ? (
        /* Grid View */
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {files.map((file) => (
            <div
              key={file.filename}
              className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              data-testid={`media-item-${file.filename}`}
            >
              {/* Image Preview */}
              <div className="aspect-square bg-gray-100 relative">
                <img
                  src={`${API_URL}${file.url}`}
                  alt={file.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%239ca3af" font-size="12">No Image</text></svg>';
                  }}
                />
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button
                    onClick={() => copyUrl(file)}
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    title={t('admin.media_page.copy_url', 'Kopírovať URL')}
                  >
                    {copiedUrl === file.filename ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4 text-gray-700" />
                    )}
                  </button>
                  <a
                    href={`${API_URL}${file.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white rounded-full hover:bg-gray-100 transition-colors"
                    title={t('admin.media_page.open', 'Otvoriť')}
                  >
                    <ExternalLink className="h-4 w-4 text-gray-700" />
                  </a>
                  <button
                    onClick={() => handleDelete(file.filename)}
                    className="p-2 bg-white rounded-full hover:bg-red-100 transition-colors"
                    title={t('admin.media_page.delete', 'Odstrániť')}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </button>
                </div>
              </div>
              
              {/* File Info */}
              <div className="p-2">
                <p className="text-xs font-medium text-gray-700 truncate" title={file.filename}>
                  {file.filename}
                </p>
                <p className="text-xs text-gray-400">{formatSize(file.size)}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.media_page.col_preview', 'Náhľad')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.media_page.col_name', 'Názov')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.media_page.col_size', 'Veľkosť')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.media_page.col_date', 'Dátum')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akcie</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {files.map((file) => (
                <tr key={file.filename} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <img
                      src={`${API_URL}${file.url}`}
                      alt={file.filename}
                      className="w-12 h-12 object-cover rounded"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23f3f4f6" width="100" height="100"/></svg>';
                      }}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-gray-900 max-w-xs truncate">
                      {file.filename}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatSize(file.size)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(file.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded max-w-[200px] block truncate">
                      {API_URL}{file.url}
                    </code>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyUrl(file)}
                      >
                        {copiedUrl === file.filename ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(file.filename)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
